import { useState, useEffect } from 'react';
import '../Industries.css';
import { useLang } from '../context/LanguageContext';
import en from '../lang/en';
import de from '../lang/de';
import it from '../lang/it';
import fr from '../lang/fr';
import { manufacturingVideo, educationVideo, dnaVideo } from '../assets/media';

const videoMap = {
  manufacturing: manufacturingVideo,
  education: educationVideo,
  dna: dnaVideo,
};


function Industries() {
  const { lang } = useLang();
  const slides = lang === 'de' ? de.industries.slides : lang === 'it' ? it.industries.slides : lang === 'fr' ? fr.industries.slides : en.industries.slides;
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [direction, setDirection] = useState('next');
  const [paused, setPaused] = useState(false);

  const goTo = (index) => {
    if (index === current) return;
    setDirection(index > current ? 'next' : 'prev');
    setCurrent(index);
    setAnimKey(k => k + 1);
  };

  const next = () => goTo((current + 1) % slides.length);
  const prev = () => goTo((current - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(next, 10000);
    return () => clearTimeout(timer);
  }, [current, paused]);

  const slide = slides[current];

  return (
    <div className="ind-page" onClick={() => setPaused(p => !p)}>
      <video key={slide.video || 'default'} ref={el => { if (el) el.playbackRate = 0.5; }} className="page-bg-video" src={videoMap[slide.video] || manufacturingVideo} autoPlay loop muted playsInline />
      <div className="page-bg-overlay"></div>
      <div className="ind-slider" key={animKey} data-dir={direction}>
        {paused && <div className="ind-paused-badge">⏸</div>}

        {/* Left — Image */}
        <div className="ind-image-side ind-img-in">
          {slide.image ? (
            <img src={slide.image} alt={slide.title} className="ind-img" />
          ) : (
            <div className="ind-img-placeholder">
              <div className="ind-placeholder-glow"></div>
              <div className="ind-quote-bg-mark">"</div>
              <div className="ind-number-badge">{slide.number}</div>
              <div className="ind-quote">
                <p className="ind-quote-text ind-qtext-in">
                  {slide.quoteBefore}{slide.quoteHighlight}{slide.quoteAfter}
                </p>
                <div className="ind-quote-divider ind-qdivider-in"></div>
                <span className="ind-quote-author ind-qauthor-in">{slide.quoteAuthor}</span>
              </div>
              {slide.services && (
                <div className="ind-services">
                  <div className="ind-services-header ind-svc-header-in">
                    <div className="ind-services-bar"></div>
                    <span className="ind-services-label">Key Services</span>
                  </div>
                  <div className="ind-services-grid">
                    {slide.services.map((s, i) => (
                      <div
                        key={i}
                        className="ind-service-item ind-svc-item-in"
                        style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                      >
                        <span className="ind-service-dot"></span>
                        <span className="ind-service-name">{s}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right — Text */}
        <div className="ind-text-side">
          <span className="ind-tag ind-tag-in">{slide.tag}</span>
          <h2 className="ind-title ind-title-in">
            <span className="ind-highlight">{slide.title}</span>
          </h2>
          <p className="ind-subtitle ind-subtitle-in">{slide.subtitle}</p>
          <p className="ind-body ind-body-in" style={{ borderLeftColor: slide.accent }}>{slide.body}</p>

          <ul className="ind-features ind-features-in">
            {slide.features.map((f, i) => (
              <li
                key={i}
                className="ind-feature-item"
                style={{ animationDelay: `${0.55 + i * 0.12}s` }}
              >
                <span className="ind-feature-icon">◈</span>
                {f.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Navigation */}
      <div className="ind-nav">
        <button className="ind-arrow" onClick={prev} aria-label="Previous">&#8592;</button>
        {slides.map((s, i) => (
          <button
            key={i}
            className={`ind-nav-label${i === current ? ' active' : ''}`}
            onClick={() => goTo(i)}
          >
            {s.title}
          </button>
        ))}
        <button className="ind-arrow" onClick={next} aria-label="Next">&#8594;</button>
      </div>
    </div>
  );
}

export default Industries;
